import React, { useLayoutEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import { createAppTheme } from "../styles/theme";
import { applyThemeVars } from "../styles/palette";
import { useThemeMode } from "./ThemeModeContext";

/**
 * Forces the dark palette for signed-out pages.
 *
 * The login screen hardcodes a dark gradient and white text, so it looks the
 * same whatever the app's theme is set to. Register did not: it used
 * `bgcolor: "background.default"` and MUI `Paper`, both driven by the theme,
 * so toggling light mode anywhere in the app turned the signup page white
 * while login stayed dark. Two halves of the same funnel, two different
 * looks, decided by a preference the visitor has usually not set yet.
 *
 * Rather than hardcoding colours a second time (which is how login and
 * register drifted apart in the first place), this scopes the DARK theme:
 *
 *  - MUI components re-theme through a nested ThemeProvider, so inputs,
 *    Paper and the stepper follow along instead of only the background.
 *  - The CSS variables are switched too, because react-jss components read
 *    `var(--c-*)` directly and would otherwise stay light inside a dark box.
 *
 * The user's own preference is untouched: nothing is written to storage, and
 * the variables are restored on unmount, so toggling light mode and then
 * signing in still lands on a light dashboard.
 */
export const AuthThemeScope: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { mode } = useThemeMode();

  useLayoutEffect(() => {
    applyThemeVars("dark");
    // Restore whatever the user actually chose when leaving the auth pages.
    // Depends on `mode` so a toggle elsewhere is still respected on the way
    // out rather than restoring a stale value.
    return () => applyThemeVars(mode);
  }, [mode]);

  // Built once: this scope is always dark, so there is nothing to recompute.
  const darkTheme = useMemo(() => createAppTheme("dark"), []);

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          minHeight: "var(--app-vh)",
          // Same gradient as the login screen, so the two steps of the same
          // funnel look like one product.
          background:
            "radial-gradient(1200px 600px at 50% -10%, #0F172A 0%, #020617 60%)",
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
};

export default AuthThemeScope;
